import { useEffect, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { downloadFile, fetchFile, isAppleMobileBrowser, sharePreparedFile, type DownloadableFile } from "./fileDownloads";
import { errorMessage } from "./format";

// iOS needs a real File and a fresh tap to open its Save to Files share sheet.
// Fetch before the tap: awaiting a network request in the click handler can lose
// Safari's transient user activation and fall back to an unusable DOCX page.
export function FileSaveButton({ file }: { file: DownloadableFile }) {
  const nativeSave = isAppleMobileBrowser();
  const [prepared, setPrepared] = useState<{ url: string | null; file: File } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    setPrepared(null); setError(null); setBusy(false);
    if (!nativeSave || !file.url) return;
    const controller = new AbortController();
    void fetchFile(file, controller.signal).then(result => {
      if (!controller.signal.aborted) setPrepared({ url: file.url, file: new File([result.blob], result.filename, { type: result.mediaType }) });
    }).catch((caught: unknown) => { if (!controller.signal.aborted) setError(errorMessage(caught)); });
    return () => { controller.abort(); };
  }, [nativeSave, file.url, file.filename, file.mediaType, retry]);

  const save = () => {
    setError(null);
    if (nativeSave) {
      if (!prepared || prepared.url !== file.url) { setRetry(value => value + 1); return; }
      // Call synchronously from this tap; do not await fetch or other work first.
      void sharePreparedFile(prepared.file).catch((caught: unknown) => { setError(errorMessage(caught)); });
    } else {
      setBusy(true);
      void downloadFile(file).catch((caught: unknown) => { setError(errorMessage(caught)); }).finally(() => { setBusy(false); });
    }
  };
  const preparing = nativeSave && (!prepared || prepared.url !== file.url);
  return <div className="file-save-control">
    <button type="button" disabled={!file.url || busy || (preparing && !error)} onClick={save}>
      {nativeSave ? <Share2 size={15} /> : <Download size={15} />}
      {busy ? "Working…" : preparing ? error ? "Retry preparing file" : "Preparing file…" : nativeSave ? "Save file" : "Download"}
    </button>
    {nativeSave && !error ? <span className="file-save-hint">Choose Save to Files in the share menu.</span> : null}
    {error ? <span className="form-error" role="alert">{error}</span> : null}
  </div>;
}
